import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  BookOpenCheck,
  RefreshCcw,
  UserCog,
  ArrowLeftRight,
  BookPlus,
  CheckCircle,
} from "lucide-react";
import Select from "react-select";
import debounce from "lodash.debounce";
import UserManager from "../../services/dashboard/UserManager";
import OrderManager from "../../services/dashboard/OrderManager";
import SubscriptionManager from "../../services/dashboard/SubscriptionManager";
import BookManager from "../../services/dashboard/BookManager";
import toast from "react-hot-toast";
import axios from "axios";
import config from "../../config";
import { RotateCcw } from "lucide-react";
import AsyncSelect from "react-select/async";
import { ReactComponent as ReturnIcon } from "../../icons/return_icons.svg";

// Enhanced Button component with modern styling
const Button = ({
  children,
  variant = "primary",
  className = "",
  icon: Icon,
  ...props
}) => {
  const baseStyles =
    "inline-flex items-center justify-center font-medium rounded-xl transition-all duration-300 gap-2 px-5 py-3 text-sm";
  const variants = {
    primary:
      "bg-gradient-to-r from-primary-shade4 to-primary hover:from-primary-shade5 hover:to-primary-shade4 text-white shadow-lg hover:shadow-primary/20",
    secondary:
      "border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800",
    success: "bg-green-600 hover:bg-green-700 text-white",
    ghost:
      "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300",
  };
  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${className}`}
      {...props}
    >
      {Icon && <Icon className="w-4 h-4" />}
      {children}
    </button>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const statusStyles = {
    ACTIVE:
      "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
    EXPIRED: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
    PENDING:
      "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
    NOT_RETURNED:
      "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
    DELIVERED:
      "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  };

  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
        statusStyles[status] || "bg-gray-100"
      }`}
    >
      {String(status).replace(/_/g, " ")}
    </span>
  );
};
const UserDetails = () => {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const BASE_URL = `${config.url}/api/orders`;
  const [isExtending, setIsExtending] = useState(false);
  // Inside component
  const [bookOptions, setBookOptions] = useState([]);
  const [bookSearchLoading, setBookSearchLoading] = useState(false);
  const [selectedBookId, setSelectedBookId] = useState(null);
  const [selectedBookOption, setSelectedBookOption] = useState(null);
  const [extendModalOpen, setExtendModalOpen] = useState(false);
  const [extendDays, setExtendDays] = useState("");
  const [selectedSubscription, setSelectedSubscription] = useState(null);

  const handleReturnRequest = async (orderId) => {
    try {
      const collectedBy = "admin"; // Or prompt or dynamically set
      await OrderManager.returnBook(orderId, collectedBy);
      toast.success(`Return initiated for order #${orderId}`);
      fetchUserDetails(); // Refresh updated data
    } catch (error) {
      console.error("Failed to process return:", error);
      toast.error(`Failed to initiate return: ${error.message || 'Unknown error'}`);
    }
  };

  const [confirmModal, setConfirmModal] = useState({
    open: false,
    message: "",
    onConfirm: null,
  });

  const handleCreateOrder = async () => {
    if (!selectedBookId) {
      toast.error("Please select a book first.");
      return;
    }

    try {
      const fullResponse = await axios.post(`${BASE_URL}/create`, null, {
        params: { userId, bookId: selectedBookId },
      });

      const { responseCode, responseMessage, responseData, errorCode } =
        fullResponse.data;

      if (responseCode !== 200) {
        // Handle known errors
        if (errorCode === "BOOK_LIMIT_EXCEEDED") {
          toast.error("User has reached the borrowing limit.");
        } else if (errorCode === "NO_ACTIVE_SUBSCRIPTION") {
          toast.error("User has no active subscription to place an order.");
        } else {
          toast.error(responseMessage || "Something went wrong.");
        }
        return;
      }

      // success
      toast.success("Order placed successfully!", {
        icon: "✅", // Optional, adds visual clarity
      });
      fetchUserDetails(); // refresh data
      setSelectedBookId(null);
    } catch (err) {
      toast.error("Something went wrong while placing the order.");
      console.error(err);
    }
  };

  const handleExtendSubscription = (sub) => {
    setSelectedSubscription(sub);
    setExtendModalOpen(true);
  };

  const handleBookSearch = async (inputValue) => {
    if (!inputValue) return;

    try {
      setBookSearchLoading(true);
      const result = await BookManager.searchBooks(inputValue);

      if (
        !result ||
        typeof result !== "object" ||
        !Array.isArray(result.responseData)
      ) {
        console.error("Invalid search result:", result);
        return;
      }

      const books = result.responseData;

      const options = books.map((book) => ({
        value: book.bookId,
        label: book.bookName,
      }));
      setBookOptions(options);
    } catch (err) {
      console.error("Error searching books", err);
    } finally {
      setBookSearchLoading(false);
    }
  };

  const handleBookSelect = (selectedOption) => {
    setSelectedBookId(selectedOption ? selectedOption.value : null);
    setSelectedBookOption(selectedOption); // ✅ This maintains the Select value
  };

  const debouncedSearch = debounce(handleBookSearch, 300);

  const fetchUserDetails = async () => {
    try {
      const selectedUser = await UserManager.getUserDetailsById(userId);
      setUser(selectedUser);

      const [userOrders, userSubscriptions] = await Promise.all([
        OrderManager.getOrdersByUser(userId),
        SubscriptionManager.getUserSubscriptions(userId),
      ]);

      setOrders(userOrders || []);
      setSubscriptions(userSubscriptions || []);
    } catch (error) {
      console.error("Error fetching user details:", error.message);
    } finally {
      setLoading(false);
    }
  };
  console.log("Book Options:", bookOptions);

  useEffect(() => {
    fetchUserDetails();
  }, [userId]);

  if (loading) return <div className="p-6">Loading user details...</div>;
  if (!user) return <div className="p-6 text-red-500">User not found</div>;

  return (
    <div className="p-6 font-nunito space-y-8 dark:bg-gray-900 min-h-screen">
      {/* Back Navigation & Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button
          variant="ghost"
          className="!px-3 !py-2"
          onClick={() => window.history.back()}
        >
          <ArrowLeftRight className="w-5 h-5 rotate-180" />
        </Button>
        <h1 className="text-3xl font-bold bg-gradient-to-r from-primary-shade4 to-primary-shade2 bg-clip-text text-transparent">
          User Management
        </h1>
      </div>

      {/* User Profile Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-3xl p-6"
      >
        <div className="flex items-center gap-6 mb-6">
          <div className="w-20 h-20 rounded-2xl bg-primary-shade1 flex items-center justify-center text-2xl font-bold text-primary">
            {user.firstName[0]}
            {user.lastName[0]}
          </div>
          <div>
            <h2 className="text-2xl font-bold dark:text-white">
              {user.firstName} {user.lastName}
            </h2>
            <p className="text-gray-500 dark:text-gray-400">{user.emailId}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          {/* User ID */}
          <div className="space-y-1">
            <p className="text-gray-500 dark:text-gray-400">User ID</p>
            <p className="font-medium dark:text-white">{user.userId}</p>
          </div>

          {/* Location */}
          <div className="space-y-1">
            <p className="text-gray-500 dark:text-gray-400">Location</p>
            <p className="font-medium dark:text-white">
              {user.city.cityName} ({user.pincode.pinCode})
            </p>
          </div>

          {/* Contact */}
          <div className="space-y-1">
            <p className="text-gray-500 dark:text-gray-400">Contact</p>
            <p className="font-medium dark:text-white">{user.mobileNumber}</p>
          </div>

          {/* Active Orders */}
          <div className="space-y-1">
            <p className="text-gray-500 dark:text-gray-400">Active Orders</p>
            <p className="font-medium dark:text-white">
              {orders.filter((o) => o.orderStatus !== "COMPLETED").length}
            </p>
          </div>

          {/* Full Delivery Address (spans all columns) */}
          <div className="col-span-2 md:col-span-4 space-y-1">
            <p className="text-gray-500 dark:text-gray-400">Delivery Address</p>
            <p className="font-medium dark:text-white">
              {user.address?.fullAddress ?? "–"}
              <br />
              {user.address?.city?.cityName}, {user.address?.pincode?.pinCode}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Orders Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-3xl p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold flex items-center gap-3 dark:text-white">
            <BookOpenCheck className="w-6 h-6 text-primary" />
            Order History
          </h3>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {orders.length} total orders
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="text-left text-sm text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
              <tr>
                <th className="pb-3">Order ID</th>
                <th className="pb-3">Book</th>
                <th className="pb-3">Status</th>
                <th className="pb-3">Return</th>
                <th className="pb-3">Delivery</th>
                <th className="pb-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y dark:divide-gray-700">
              {orders.map((order) => (
                <tr
                  key={order.orderId}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50"
                >
                  <td className="py-4 font-medium dark:text-white">
                    #{order.orderId}
                  </td>
                  <td className="py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-primary-shade1" />
                      <span className="font-medium dark:text-white">
                        {order.book?.bookName}
                      </span>
                    </div>
                  </td>
                  <td className="py-4">
                    <StatusBadge status={order.orderStatus} />
                  </td>
                  <td className="py-4">
                    <StatusBadge status={order.returnStatus} />
                  </td>
                  <td className="py-4">
                    <StatusBadge status={order.deliveryStatus} />
                  </td>
                  <td className="py-4">
                    {order.orderStatus === "ACTIVE" && 
                     order.returnStatus !== "REQUESTED" && 
                     order.returnStatus !== "PICKUP_SCHEDULED" && 
                     order.returnStatus !== "IN_TRANSIT" && 
                     order.returnStatus !== "RETURNED" ? (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() =>
                          setConfirmModal({
                            open: true,
                            message: `Are you sure you want to initiate return for Order #${order.orderId}?`,
                            onConfirm: () => handleReturnRequest(order.orderId),
                          })
                        }
                        className="flex items-center border border-primary gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-all duration-200 hover:scale-105 hover:shadow-md"
                      >
                        <ReturnIcon className="w-4 h-4" />
                        Initiate Return
                      </Button>
                    ) : order.returnStatus === "REQUESTED" ? (
                      <span className="text-sm text-orange-600 dark:text-orange-400 font-medium">
                        Return Requested
                      </span>
                    ) : order.returnStatus === "PICKUP_SCHEDULED" ? (
                      <span className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                        Pickup Scheduled
                      </span>
                    ) : order.returnStatus === "IN_TRANSIT" ? (
                      <span className="text-sm text-purple-600 dark:text-purple-400 font-medium">
                        In Transit
                      </span>
                    ) : order.returnStatus === "RETURNED" ? (
                      <span className="text-sm text-green-600 dark:text-green-400 font-medium">
                        ✓ Returned
                      </span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Create Order Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-3xl p-6"
      >
        <h3 className="text-xl font-semibold mb-6 flex items-center gap-3 dark:text-white">
          <BookPlus className="w-6 h-6 text-primary" />
          Create New Order
        </h3>

        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
          <div className="w-full md:w-96">
            <AsyncSelect
              cacheOptions
              defaultOptions
              loadOptions={async (inputValue) => {
                if (!inputValue) return [];
                const result = await BookManager.searchBooks(inputValue);
                if (!result?.responseData) return [];
                return result.responseData.map((b) => ({
                  value: b.bookId,
                  label: b.bookName,
                }));
              }}
              placeholder="Search books..."
              isClearable
              value={selectedBookOption}
              onChange={handleBookSelect}
              styles={{
                control: (base) => ({
                  ...base,
                  borderRadius: "12px",
                  padding: "6px",
                  borderColor: "#e5e7eb",
                  "&:hover": { borderColor: "#FF7236" },
                }),
              }}
            />
          </div>

          <AnimatePresence>
            {selectedBookId && (
              <motion.div
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-center gap-3 text-sm text-green-600 font-medium"
              >
                <CheckCircle className="w-5 h-5" />
                Selected:{" "}
                {bookOptions.find((b) => b.value === selectedBookId)?.label}
              </motion.div>
            )}
          </AnimatePresence>

          <Button
            variant="primary"
            onClick={() =>
              setConfirmModal({
                open: true,
                message: "Are you sure you want to create this order?",
                onConfirm: handleCreateOrder,
              })
            }
            icon={BookPlus}
            className="w-full md:w-auto"
          >
            Create Order
          </Button>
        </div>
      </motion.div>

      {/* Subscriptions Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-3xl p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-semibold flex items-center gap-3 dark:text-white">
            <RefreshCcw className="w-6 h-6 text-primary" />
            Subscription Plans
          </h3>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {subscriptions.filter((s) => s.status === "ACTIVE").length} active
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {subscriptions.map((sub) => (
            <div
              key={sub.id}
              className={`p-4 rounded-xl border ${
                sub.status === "ACTIVE"
                  ? "border-primary/20 bg-primary/5"
                  : "border-gray-200 dark:border-gray-700"
              }`}
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h4 className="font-semibold dark:text-white">
                    {sub.planName}
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {sub.packageType}
                  </p>
                </div>
                <StatusBadge status={sub.status} />
              </div>
              <div className="space-y-2 text-sm">
                <p className="dark:text-gray-300">
                  <span className="text-gray-500 dark:text-gray-400">
                    Ends:
                  </span>{" "}
                  {sub.endDate?.join("-")}
                </p>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-500"
                    style={{ width: `${calculateSubscriptionProgress(sub)}%` }}
                  />
                </div>
                {/* Show extend button */}
                {sub.status === "ACTIVE" && (
                  <div className="mt-3">
                    <Button
                      variant="secondary"
                      className="w-full"
                      onClick={() =>
                        setConfirmModal({
                          open: true,
                          message: `Extend subscription: ${sub.planName}?`,
                          onConfirm: () => handleExtendSubscription(sub),
                        })
                      }
                    >
                      Extend Subscription
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </motion.div>
      {extendModalOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-xl w-80 space-y-4">
            <h3 className="text-xl font-semibold dark:text-white">
              Extend Subscription
            </h3>

            <input
              type="number"
              className="w-full border dark:border-gray-700 dark:bg-gray-900 rounded-lg px-4 py-2"
              placeholder="Enter days"
              value={extendDays}
              onChange={(e) => setExtendDays(e.target.value)}
            />

            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => {
                  if (!isExtending) setExtendModalOpen(false);
                }}
              >
                Cancel
              </Button>

              <Button
                variant="primary"
                disabled={isExtending}
                onClick={async () => {
                  if (!extendDays || isNaN(extendDays)) {
                    toast.error("Enter valid days");
                    return;
                  }

                  try {
                    setIsExtending(true);

                    await SubscriptionManager.extendSubscription(
                      userId,
                      parseInt(extendDays)
                    );

                    toast.success("Subscription extended!");
                    setExtendModalOpen(false);
                    setExtendDays("");
                    fetchUserDetails();
                  } catch (err) {
                    toast.error(
                      err.response?.data?.responseMessage || "Failed"
                    );
                  } finally {
                    setIsExtending(false);
                  }
                }}
                className="relative"
              >
                {isExtending ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  "Extend"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
      {confirmModal.open && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-xl w-80 space-y-4">
            <h3 className="text-lg font-semibold dark:text-white">
              Confirmation
            </h3>

            <p className="text-gray-700 dark:text-gray-300">
              {confirmModal.message}
            </p>

            <div className="flex justify-end gap-3 pt-3">
              <Button
                variant="secondary"
                onClick={() => setConfirmModal({ open: false })}
              >
                Cancel
              </Button>

              <Button
                variant="primary"
                onClick={() => {
                  confirmModal.onConfirm();
                  setConfirmModal({ open: false });
                }}
              >
                Yes, Proceed
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function for subscription progress
const calculateSubscriptionProgress = (subscription) => {
  const start = new Date(subscription.startDate?.join("-"));
  const end = new Date(subscription.endDate?.join("-"));
  const now = new Date();
  const total = end - start;
  const elapsed = now - start;
  return Math.min((elapsed / total) * 100, 100);
};

export default UserDetails;
